import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, 
  CircularProgress, Alert, Grid, Divider, Breadcrumbs, Link
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Print as PrintIcon, 
  ArrowBack as BackIcon,
  LocalShipping as ShippingIcon,
  Person as DriverIcon,
  CalendarToday as DateIcon,
  LocationCity as CityIcon
} from '@mui/icons-material';
import tripService from '../services/tripService';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const getStatusColor = (status) => {
  switch (status) {
    case 'borrador': return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
    case 'en_curso': return { bg: '#e3f2fd', text: '#1976d2', border: '#90caf9' };
    case 'finalizado': return { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' };
    case 'cancelado': return { bg: '#ffebee', text: '#d32f2f', border: '#ef9a9a' };
    default: return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'borrador': return 'Borrador';
    case 'en_curso': return 'En Curso';
    case 'finalizado': return 'Finalizado';
    case 'cancelado': return 'Cancelado';
    default: return status;
  }
};

const TripDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [trip, setTrip] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const tripData = await tripService.getTripDetail(id);
      setTrip(tripData);
      const reportData = await tripService.getTripReport(id);
      setReport(reportData);
    } catch (err) {
      setError('Error al cargar el detalle del viaje.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: '#800020' }} />
        </Box>
      </Box>
    );
  }

  if (error || !trip) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
        <Sidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, p: 4 }}>
          <Alert severity="error">{error || 'Viaje no encontrado'}</Alert>
        </Box>
      </Box>
    );
  }

  const statusStyle = getStatusColor(trip.status);

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }} className="main-box">
      <Box className="no-print">
        <Sidebar onLogout={handleLogout} />
      </Box>
      
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }} className="print-container">
        {/* Breadcrumbs - No visible on print */}
        <Breadcrumbs sx={{ mb: 3 }} className="no-print">
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/trips'); }}
            sx={{ color: '#4e7397', fontSize: '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Viajes
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Detalle #{trip.id}
          </Typography>
        </Breadcrumbs>

        {/* Header - No visible on print */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: 4, gap: 2 }} className="no-print">
          <IconButton onClick={() => navigate('/trips')} sx={{ bgcolor: 'white', border: '1px solid #d0dbe7', color: '#0e141b' }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h3" sx={{ color: '#0e141b', fontSize: { xs: '1.875rem', md: '2.25rem' }, fontWeight: 900, mb: 0.5, letterSpacing: '-0.033em' }}>
              Viaje #{trip.id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={getStatusLabel(trip.status)} 
                size="small"
                sx={{ 
                  bgcolor: statusStyle.bg, 
                  color: statusStyle.text, 
                  fontWeight: 700, 
                  border: `1px solid ${statusStyle.border}`,
                  fontSize: '0.75rem'
                }} 
              />
              <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                Destino: {trip.city}
              </Typography>
            </Box>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ 
              bgcolor: '#0e141b', 
              '&:hover': { bgcolor: '#000' },
              borderRadius: '0.5rem',
              px: 3,
              fontWeight: 700,
              textTransform: 'none'
            }}
          >
            Imprimir Planilla
          </Button>
        </Box>

        {/* Content Area */}
        <Paper 
          sx={{ 
            p: { xs: 3, md: 5 }, 
            borderRadius: '1rem', 
            border: '1px solid #d0dbe7', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            bgcolor: 'white'
          }} 
          className="report-paper"
        >
          {/* Internal Header for Print */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 5, borderBottom: '2px solid #800020', pb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#0e141b', mb: 1, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>PLANILLA DE REPARTO</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateIcon sx={{ color: '#4e7397', fontSize: '1rem' }} />
                  <Typography sx={{ fontWeight: 700 }}>{trip.date.split('-').reverse().join('/')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CityIcon sx={{ color: '#4e7397', fontSize: '1rem' }} />
                  <Typography sx={{ fontWeight: 700 }}>{trip.city}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, color: '#800020' }}>VIAJE #{trip.id}</Typography>
              <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Sistema de Gestión Capriata</Typography>
            </Box>
          </Box>

          {/* Info Summary */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '0.75rem', borderLeft: '4px solid #800020' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700, mb: 1 }}>Repartidor</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DriverIcon sx={{ color: '#0e141b' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{trip.driver?.first_name || 'Sin Asignar'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '0.75rem', borderLeft: '4px solid #800020' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700, mb: 1 }}>Pedidos Totales</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ShippingIcon sx={{ color: '#0e141b' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{report?.orders?.length || 0} entregas</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} className="no-print">
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '0.75rem', borderLeft: '4px solid #800020' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700, mb: 1 }}>Carga Total</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', pl: 0.5 }}>
                  {report?.orders?.reduce((acc, o) => acc + (parseFloat(o.total_weight) || 0), 0).toFixed(2)} kg
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="h5" sx={{ fontWeight: 900, mb: 3, color: '#0e141b', borderBottom: '1px solid #f0f0f0', pb: 1 }}>
            Hoja de Ruta y Entregas
          </Typography>

          {/* Orders detail */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {report?.orders?.map((orderInfo, index) => (
              <Box key={orderInfo.order_number} sx={{ pageBreakInside: 'avoid' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ width: 32, height: 32, bgcolor: '#0e141b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontWeight: 900, fontSize: '1rem' }}>
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: '#0e141b' }}>{orderInfo.client_name}</Typography>
                      <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', fontWeight: 600 }}>{orderInfo.address}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Pedido #{orderInfo.order_number}</Typography>
                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Tel: {orderInfo.phone}</Typography>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #f0f0f0', borderRadius: '0.5rem', mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4e7397' }}>PRODUCTO</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4e7397' }}>CANTIDAD</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4e7397', width: { xs: 70, md: '150px' } }}>ESTADO ENTREGA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderInfo.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 600 }}>{item.product_name}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{item.quantity}</TableCell>
                          <TableCell sx={{ borderLeft: '1px solid #f0f0f0' }}></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Box sx={{ borderBottom: '1px dotted #ccc', minHeight: '40px', display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#4e7397', fontSize: '0.7rem' }}>Observaciones:</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ borderBottom: '1px dotted #ccc', minHeight: '40px', display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#4e7397', fontSize: '0.7rem' }}>Firma Conformidad:</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>

          {report?.orders?.length === 0 && (
            <Box sx={{ py: 10, textAlign: 'center' }}>
              <ShippingIcon sx={{ fontSize: 64, color: '#d0dbe7', mb: 2 }} />
              <Typography sx={{ color: '#4e7397' }}>No hay pedidos asignados a este viaje.</Typography>
            </Box>
          )}

          {/* Special notes for print */}
          <Box className="print-only" sx={{ display: 'none', mt: 10, pt: 5, borderTop: '2px solid #f0f0f0' }}>
            <Grid container spacing={6}>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #000', mt: 8, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700 }}>Firma del Chofer</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #000', mt: 8, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700 }}>Despacho / Salida</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>

      {/* Global Print Styles */}
      <style>{`
        @media print {
          title { visibility: hidden; }
          .no-print { display: none !important; }
          .main-box { background-color: white !important; display: block !important; }
          .print-container { padding: 0 !important; margin: 0 !important; max-width: 100% !important; flex: none !important; }
          .report-paper { border: none !important; padding: 0 !important; box-shadow: none !important; }
          .MuiTableRow-root { break-inside: avoid; }
          .print-only { display: block !important; }
          @page { margin: 1.5cm; }
        }
      `}</style>
    </Box>
  );
};

export default TripDetailPage;
